import { SaveIcon } from '@systemeio/ui/icons';
import { PrimaryButton, toast } from '@systemeio/ui';
import { createGlobalAvailability, updateGlobalAvailability } from 'modules/calendar/availability/api/availability-api';
import { DateSpecificAvailabilitySlots } from 'modules/calendar/availability/components/date-specific-availability-slots';
import { DateUnavailabilitySlots } from 'modules/calendar/availability/components/date-unavailability-slots';
import { WeekdaysAvailabilitySlots } from 'modules/calendar/availability/components/weekdays-availability-slots';
import { useDraftAvailabilitySlots, useDraftUnavailabilitySlots } from 'modules/calendar/availability/hooks/use-draft-availability-slots';
import { useGlobalAvailability } from 'modules/calendar/availability/hooks/use-global-availabilities';
import { AvailabilityErrors } from 'modules/calendar/availability/types/availability-interface';
import TabMenu from 'modules/calendar/components/tab-menu';
import { availabilityLoco } from 'modules/calendar/constants/calendar-loco';
import { CalendarSectionsLocoKeys } from 'modules/calendar/enums/calendar-sections-enum';
import { NextPage } from 'next';
import { useState } from 'react';
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { handle422Error } from 'shared/utils/parse-422-errors';
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
const Availability: NextPage = () => {
  const {
    user
  } = useUser();
  const {
    t
  } = useLocoTranslation();
  const [errors, setErrors] = useState<AvailabilityErrors>();
  const {
    availability,
    isLoading,
    mutate
  } = useGlobalAvailability(true);
  const {
    revalidateModules
  } = useRevalidateModules();
  const {
    draftAvailabilitySlots,
    weekdaysSlots,
    dateSpecificSlots,
    addSlot,
    removeSlots,
    updateSlot,
    reinitiate
  } = useDraftAvailabilitySlots(availability?.dailySettings);
  const unavailabilityDraft = useDraftUnavailabilitySlots(availability?.unavailabilityRanges);
  const [isFetching, setIsFetching] = useState(false);
  const isEditMode = Boolean(availability && (availability.dailySettings.length !== 0 || availability.unavailabilityRanges.length !== 0));
  const saveChanges = async () => {
    try {
      setErrors(undefined);
      setIsFetching(true);
      if (user) {
        const data = {
          timezone: user.timezone,
          dailySettings: draftAvailabilitySlots.map(slot => ({
            date: slot.date,
            dayOfWeek: slot.dayOfWeek,
            startTime: slot.startTime,
            endTime: slot.endTime
          })),
          unavailabilityRanges: unavailabilityDraft.slots.map(slot_0 => ({
            startDate: slot_0.startDate,
            endDate: slot_0.endDate
          }))
        };
        isEditMode ? await updateGlobalAvailability(data) : await createGlobalAvailability(data);
        await revalidateModules([RequestModuleEnum.AvailabilityBookings, RequestModuleEnum.CalendarBookings], true);
        await mutate();
        reinitiate();
        unavailabilityDraft.reinitiate();
        toast.success(t('dashboard.calendar.global_availability_update_success'));
      }
    } catch (e) {
      handle422Error(e, setErrors);
    } finally {
      setIsFetching(false);
    }
  };
  const fieldPrefixes = ['weeklyDailySettings[', 'dateSpecificDailySettings[', 'unavailabilityRanges['];
  const filteredErrors = errors && Object.keys(errors).filter(key => !fieldPrefixes.some(prefix => key.startsWith(prefix))).reduce((acc, key_0) => {
    acc[key_0 as keyof AvailabilityErrors] = errors[key_0 as keyof AvailabilityErrors];
    return acc;
  }, {} as AvailabilityErrors);
  return <>
      <TabMenu />
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <h1>{t(CalendarSectionsLocoKeys.availability)}</h1>
          <div>
            <PrimaryButton isFetching={isFetching || isLoading} onClick={saveChanges} className="ml-auto w-fit">
              <SaveIcon />
              {t('global.save')}
            </PrimaryButton>
            <FieldErrorAndDescription error={filteredErrors && Object.values(filteredErrors).filter((error): error is string => typeof error === 'string').map(error_0 => error_0.endsWith('.') ? error_0 : `${error_0}.`).join('\n')} errorClassName={'pt-2'} />
          </div>
        </div>
        <div className="flex w-full flex-col gap-10 xl:flex-row">
          <div className="bg-background-secondary w-full rounded-md p-5 xl:w-1/2">
            <WeekdaysAvailabilitySlots slots={weekdaysSlots} addSlot={addSlot} updateSlot={updateSlot} removeSlots={removeSlots} isLoading={isLoading} titleClassName="text-lg text-text-primary" errors={errors} />
          </div>
          <div className="flex w-full flex-col gap-10 xl:w-1/2">
            <div className="bg-background-secondary rounded-md p-5">
              <DateSpecificAvailabilitySlots slots={dateSpecificSlots} addSlot={addSlot} updateSlot={updateSlot} removeSlots={removeSlots} isLoading={isLoading} disabled={isLoading} titleClassName="text-lg text-text-primary" errors={errors} />
            </div>
            <div className="bg-background-secondary rounded-md p-5">
              <DateUnavailabilitySlots slots={unavailabilityDraft.slots} addSlot={unavailabilityDraft.addSlot} updateSlot={unavailabilityDraft.updateSlot} removeSlot={unavailabilityDraft.removeSlot} isLoading={isLoading} disabled={isLoading} titleClassName="text-lg text-text-primary" errors={errors} />
            </div>
          </div>
        </div>
      </div>
    </>;
};
export const getServerSideProps = getBaseServerSideProps(availabilityLoco, 'dashboard.calendar.title_availability');
export default Availability;